import { TocSection } from '@squareup/dex-types-shared-docs';
import { NullableClassName } from '@squareup/dex-types-shared-ui';
import { shimStyles, SidebarLink, StickyContainer } from '@squareup/dex-ui';
import { Box, Heading5 } from '@squareup/dex-ui-shared-base';
import clsx from 'clsx';
import React, {
  FC,
  PropsWithChildren,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { observe } from 'react-intersection-observer';

import styles from './floating-toc.module.css';

interface FloatingTocProps {
  sections: Array<TocSection>;
  mainContentRef: RefObject<HTMLElement>;
}

/**
 * Gets the largest item in a map
 * @param map
 * @returns The largest item in the map, or undefined
 */
function getLargest(map: Map<string, number>): number | undefined {
  if (map.size === 0) {
    return undefined;
  }

  const values = [...map.values()];

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  let max = values[0]!;
  for (const value of values) {
    if (value > max) {
      max = value;
    }
  }

  return max;
}

/**
 * The Floating TOC's job is to be a sticky TOC that highlights
 * the link of the section you are currently looking at.
 */
const FloatingToc: FC<
  PropsWithChildren<FloatingTocProps & NullableClassName>
> = ({ sections, mainContentRef, className }) => {
  const ftocScrollAreaRef = useRef<HTMLElement>(null);

  // Keep this as a ref to avoid re-rendering
  const inViewMap = useRef<Map<string, number>>(new Map());
  const [hasShim, setHasShim] = useState(false);

  useEffect(() => {
    if (!mainContentRef.current) {
      return () => null;
    }

    // eslint-disable-next-line unicorn/prefer-spread
    const anchors = Array.from(
      ftocScrollAreaRef.current?.querySelectorAll('a') || []
    );
    const cleanupObservers: Array<() => void> = [];
    function handleHighlights() {
      const elements = sections
        .map((section) => {
          try {
            // If we fail to run the query selector due to a bad hash id,
            // avoid crashing the whole page and instead eat the error.
            return mainContentRef.current?.querySelector(`#${section.id}`);
          } catch {
            return undefined;
          }
        })
        .filter((element): element is Element => Boolean(element));

      // When we intersect anchors, we always mark them as scrolled past
      // Then, we find the last one we scrolled past, and highlight it
      const onIntersect = (
        elementInView: boolean,
        entry: IntersectionObserverEntry,
        index: number
      ) => {
        if (elementInView) {
          inViewMap.current.set(entry.target.id, index);
        } else {
          inViewMap.current.delete(entry.target.id);
        }

        const max = getLargest(inViewMap.current);

        // To avoid lots of re-renders, we will instead add the class-list
        // using normal DOM APIs
        // The logic here is that we'll iterate over all anchors, turning off
        // the selected state unless it's the largest index we've scrolled past, indicating
        // that it's closer to the bottom. In other words, the last item we scrolled past that is
        // closest to the bottom is selected
        for (const [anchorIndex, anchor] of anchors.entries()) {
          if (anchorIndex === max) {
            anchor?.classList.add(styles.selected as string);
          } else {
            anchor?.classList.remove(styles.selected as string);
          }
        }
      };

      for (let i = 0; i < elements.length; i++) {
        // Store to avoid closure issues
        const index = i;
        const element = elements[index];
        const cleanup = observe(
          element as Element,
          (elementInView, entry) => onIntersect(elementInView, entry, index),
          // The top expands upwards forever, which means we're always intersecting once we pass the anchor
          // The bottom contracts by 90%, meaning it's only intersecting once it's almost all the way up your screen
          { rootMargin: '10000000px 0px -90% 0px' }
        );

        cleanupObservers.push(cleanup);
      }
    }

    function handleScrollBottom() {
      const lastAnchor = anchors?.[anchors?.length - 1];
      if (!lastAnchor) {
        return;
      }

      const cleanup = observe(lastAnchor, (inView) => {
        if (inView) {
          setHasShim(false);
        } else {
          setHasShim(true);
        }
      });

      cleanupObservers.push(cleanup);
    }

    handleHighlights();
    handleScrollBottom();

    return () => {
      cleanupObservers.forEach((cleanup) => cleanup());
    };
  }, [mainContentRef, sections, inViewMap, setHasShim]);

  if (sections.length === 0) {
    return null;
  }

  const lastSelected = getLargest(inViewMap.current);

  return (
    <>
      <StickyContainer
        scrollableRef={ftocScrollAreaRef}
        className={clsx(styles['floating-toc'], className)}
        testId={'ftoc'}
      >
        <Heading5>On this page</Heading5>
        <Box
          ref={ftocScrollAreaRef}
          margin={{ vertical: '1x' }}
          className={clsx(styles['scrollable-el'], hasShim && shimStyles.shim)}
          testId={'ftoc-scroll'}
        >
          {sections.map((section, index) => {
            return (
              <SidebarLink
                className={clsx(
                  styles.item,
                  lastSelected === index && styles.selected
                )}
                key={`${section.name}_${index}`}
                href={`#${section.id}`}
                trackingId={'floating-toc-item-clicked'}
                margin={{ bottom: '1x' }}
              >
                {section.name}
              </SidebarLink>
            );
          })}
        </Box>
      </StickyContainer>
    </>
  );
};

export { FloatingToc };
